import { useEffect, useState } from 'react'
import { APP_EVENTS } from '../../../../../constants'
import { AddressType, AddressTypeLabel, CustomerAddressDto, TempCustomerAddressDto } from '../../../../../definition'
import { appEventEmitter } from '../../../../../initialization'
import { ModalNode } from '../../../../components/modal.component'
import { AddressDetail, AddressDetailProps } from './address-detail'
import { AddressList, AddressListProps } from './address-list'
import PlusIcon from '../../../../../styles/images/plus-icon.png'
import { asyncGetCustomerAddress } from '../../../../../services'
import { InputBase } from '../../../../../components'

interface CustomerAddressProps {
  addressType: AddressType
  accountId?: string
  isCreatingCustomer?: boolean
  getTemporaryAddress?: (address: TempCustomerAddressDto) => void
  temporaryAddress?: TempCustomerAddressDto | null
  temporaryDesignAddress?: TempCustomerAddressDto | null
  setBillingSameAsDesign?: () => void
  bookingAddress?: CustomerAddressDto
  setSelectAddress?: (address: CustomerAddressDto, addressType: AddressType, index?: number) => void
  isEditable?: boolean
  regetDefaultAddress?: (accountId: string, addressType: AddressType) => void
  isBillingSameAsDesign?: boolean
  toggleBillingSameAsDesign?: () => void
}

export default function CustomerAddress(props: CustomerAddressProps) {
  const [addresses, setAddress] = useState<CustomerAddressDto[] | []>([])

  useEffect(() => {
    fetchAddressList()
  }, [props.accountId])

  const fetchAddressList = async () => {
    const data = await asyncGetCustomerAddress({ limit: 20, offset: 0 }, props.addressType, props.accountId || '')
    setAddress(data?.items || [])
  }

  const onManageAddress = () => {
    appEventEmitter.emit<ModalNode<AddressListProps>>(APP_EVENTS.app_modal, {
      input: {
        addressList: addresses,
        addressType: props.addressType,
        accountId: props.accountId,
        refreshData: fetchAddressList,
        bookingAddress: props.bookingAddress,
        regetDefaultAddress: props.regetDefaultAddress,
        setSelectAddress: props.setSelectAddress
      },
      status: true,
      node: AddressList,
      title: `Manage ${AddressTypeLabel[props.addressType]} Address`,
      onClose: () => {},
      onDone: () => {}
    })
  }

  const onCreateNewAddress = () => {
    appEventEmitter.emit<ModalNode<AddressDetailProps>>(APP_EVENTS.app_modal, {
      status: true,
      node: AddressDetail,
      title: `Create New ${AddressTypeLabel[props.addressType]} Address`,
      input: {
        isNew: true,
        addressType: props.addressType,
        accountId: props.accountId,
        isCreatingCustomer: props.isCreatingCustomer,
        getTemporaryAddress: props.getTemporaryAddress,
        isFirstAddress: addresses.length === 0
      },
      onClose: () => {},
      onDone: async () => {
        await fetchAddressList()
        if (props.accountId && !props.bookingAddress) {
          props.regetDefaultAddress?.(props.accountId, props.addressType)
        }
      }
    })
  }

  const onReselectBookingAddress = () => {
    appEventEmitter.emit<ModalNode<AddressListProps>>(APP_EVENTS.app_modal, {
      status: true,
      node: AddressList,
      input: {
        addressList: addresses,
        addressType: props.addressType,
        accountId: props.accountId,
        refreshData: fetchAddressList,
        selectOnly: true,
        setSelectAddress: props.setSelectAddress
      },
      title: 'Select Address',
      onClose: () => {},
      onDone: () => {
        fetchAddressList()
      }
    })
  }

  if (props.isBillingSameAsDesign) {
    return (
      <div className='customer-address'>
        <div className='customer-address__detail'>
          <div className='customer-address-header'>
            <label className='customer-address-title'>{AddressTypeLabel[props.addressType]} Address</label>
            {props.addressType === AddressType.Billing && (
              <div className='customer-address__same-content'>
                <InputBase
                  value=''
                  inputType='checkbox'
                  checked={props.isBillingSameAsDesign}
                  onChange={() => props.toggleBillingSameAsDesign?.()}
                  disabled={!props.isEditable}
                />
                <span>Same as designated address</span>
              </div>
            )}
          </div>
          <div className='customer-address-content'>
            {props.bookingAddress ? (
              <>
                {props.isEditable && (
                  <>
                    <label>Address Name</label>: <span>{props.bookingAddress?.addressName}</span>
                  </>
                )}
                <p>{props.bookingAddress?.addressLine1}</p>
                <p>{props.bookingAddress?.unitNo}</p>
                <p>{props.bookingAddress?.addressPostalCode}</p>
                <p>Residential Type: {props.bookingAddress?.residentialType}</p>
              </>
            ) : null}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className='customer-address'>
      <div className='customer-address__detail'>
        <div className='customer-address-header'>
          <label className='customer-address-title'>{AddressTypeLabel[props.addressType]} Address</label>
          {props.addressType === AddressType.Billing && props.isEditable && (
            <div className='customer-address__same-content'>
              <InputBase
                value=''
                inputType='checkbox'
                checked={props.isBillingSameAsDesign}
                onChange={() => props.toggleBillingSameAsDesign?.()}
                disabled={!props.isEditable}
              />
              <span>Same as designated address</span>
            </div>
          )}
        </div>
        <div className='customer-address-content'>
          {props.bookingAddress ? (
            <>
              {props.isEditable && (
                <>
                  <label>Address Name</label>: <span>{props.bookingAddress?.addressName}</span>
                </>
              )}
              <p>{props.bookingAddress?.addressLine1}</p>
              <p>{props.bookingAddress?.unitNo}</p>
              <p>{props.bookingAddress?.addressPostalCode}</p>
              <p>Residential Type: {props.bookingAddress?.residentialType}</p>
            </>
          ) : (
            <div
              className='customer-address__add empty-primary-message'
              onClick={() => {
                if (addresses.length > 0) {
                  onReselectBookingAddress()
                } else {
                  onCreateNewAddress()
                }
              }}
            >
              <img src={PlusIcon} alt='plus icon' />
              {addresses.length > 0 ? 'Select Address' : 'Add New Address'}
            </div>
          )}
        </div>
        <div className='customer-address-action action-button'>
          {props.isEditable && (
            <button style={{ width: 200 }} type='button' className='secondary' onClick={() => onManageAddress()}>
              Manage {AddressTypeLabel[props.addressType]} Address
            </button>
          )}
          {props.bookingAddress && props.isEditable && (
            <button
              type='button'
              style={{ width: 200 }}
              className='secondary'
              onClick={() => onReselectBookingAddress()}
            >
              Re-Select Address
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
